import * as XlsxPopulate from 'xlsx-populate/browser/xlsx-populate';
import { t } from 'i18next';

import { AppConfigurationStore } from '../stores/AppConfigurationStore';
import { PersistentStore } from '../stores/PersistentStore';
import { UIHelper } from './UIHelper';
import { ScreenshotHelper } from './ScreenshotHelper';
import { ToggleStoreState } from '../stores/ToggleStore';
import { ProjectManagerStore } from '../stores/ProjectManagerStore';
import { ArticleActions } from '../actions/ArticleActions';
import { PDFMaker } from './PDFMaker';
import { ArticleStore } from '../stores/ArticleStore';
import { Workbook, XLSXDocument } from './XLSXDocument';
import { DownloadHelper } from './DownloadHelper';
import { ExportGeometryFormat } from '../constants/ExportFormats';
import { DialogActions } from '../actions/DialogActions';
import { Stats } from './Stats';
import { UrlHelper } from '../utils/UrlHelper';

import utils = egr.wcf.utils;

import { MailOptions } from '../submodules/app-api/AppAction';
import { writeFile } from '../submodules/app-api/Filesystem/Helper';

export interface ExportMapping {
    format: string;
    url: string;
}

export interface MailShareContent {
    planning?: string;
    cad?: Array<ExportMapping>;
    session?: Array<ExportMapping>;
    // attachments
    image?: ArrayBuffer;
    articleList?: ArrayBuffer;
    excel?: ArrayBuffer;
}

export abstract class MailHelper {
    public static getMailLink(): string {
        if (AppConfigurationStore.getState().os === 'AndroidOS' && AppConfigurationStore.getState().inApp) {
            return ('http://www.easterngraphics.com/' + PersistentStore.getState().language + '/produkte/pconbox.html');
        }
        return 'mailto:box-support@easterngraphics.com';
    }

    public static async generateMailShareContent(exportSettings: ToggleStoreState, killProcess: () => boolean): Promise<MailShareContent | null> {
        const filename: string = ProjectManagerStore.getProjectName(true);
        const shareContent: MailShareContent = {};
        let itemCount: number = 0;
        if (killProcess != null && killProcess()) {
            return null;
        }

        await ArticleActions.updateArticleList();

        // add Screenshot
        if (killProcess != null && killProcess()) {
            return null;
        }

        const pboxUrl: string = await DownloadHelper.exportPBox();
        shareContent.planning = UrlHelper.getCurrentLink({
            queryArguments: {
                pbox: pboxUrl
            },
            ignoreHashArguments: true,
            hashArguments: {}
        });

        // add Screenshot
        if (killProcess != null && killProcess()) {
            return null;
        }

        if (exportSettings.indexOf('IMAGE') !== -1) {
            const image: string = await ScreenshotHelper.getScreenshot({
                width: 1920,
                height: 1080,
                stripUrlScheme: false,
                renderAsync: true,
                addWatermark: true,
                customMimetype: true
            });
            shareContent.image = utils.arraybuffer.fromDataUri(image);
            itemCount++;
        }

        // add PDF Article List
        if (killProcess != null && killProcess()) {
            return null;
        }

        if (exportSettings.indexOf('PDF') !== -1) {
            shareContent.articleList = await PDFMaker.generatePDFAsBuffer(true);
            itemCount++;
        }

        // add Excel Article List
        if (killProcess != null && killProcess()) {
            return null;
        }

        if (exportSettings.indexOf('EXCEL') !== -1) {
            const xlsx: XLSXDocument = new XLSXDocument(ArticleStore.getState().articleList);
            const workbook: Workbook = await xlsx.getDocument(UIHelper.showPrices());
            shareContent.excel = await workbook.outputAsync('arraybuffer') as ArrayBuffer;
            itemCount++;
        }

        // upload the image and article list if needed (if not in app mode)
        if (killProcess != null && killProcess()) {
            return null;
        }

        // generate the cad files
        const cadSetting: ToggleStoreState = exportSettings.filter((setting) => {
            return setting === '3DS' || setting === 'DWG' || setting === 'SKP' || setting === 'FBX' || setting === 'Revit';
        });

        if (cadSetting.length) {
            shareContent.cad = [];
            const exportSettingsArray: Array<Array<string>> = cadSetting.map((value: string): Array<string> => {
                return ExportGeometryFormat[value];
            });
            let exportedGeometries: Array<string> = [];

            if (killProcess != null && killProcess()) {
                return null;
            }

            try {
                exportedGeometries = await DownloadHelper.exportGeometries(exportSettingsArray);
            } catch (e) {
                let label: string = t('The CAD file could not be generated.');
                if (cadSetting.length > 1) {
                    label = t('The CAD files could not be generated.');
                }
                const continueProgress: boolean = await DialogActions.getPromiseDialog(label);
                if (!continueProgress) {
                    return null;
                }
            }

            for (let i: number = 0; i < exportedGeometries.length; i++) {
                if (killProcess != null && killProcess()) {
                    return null;
                }

                try {
                    shareContent.cad.push({
                        format: cadSetting[i],
                        url: await DownloadHelper.uploadUrlToFileStorage(exportedGeometries[i], filename)
                    });
                } catch (e) {
                    const continueProgress: boolean = await DialogActions.getPromiseDialog(
                        t('The CAD file could not be generated.') + ' (' + cadSetting[i] + ')'
                    );
                    if (!continueProgress) {
                        return null;
                    }
                }
            }
            itemCount += shareContent.cad.length;
        }

        // Generate the store the session export files
        const sessionExports: Array<ExportMapping> = [];

        if (killProcess != null && killProcess()) {
            return null;
        }

        if (exportSettings.indexOf('OBX') !== -1) {
            sessionExports.push(
                await DownloadHelper.exportObx().then(
                    async (url: string) => {
                        return {
                            format: 'OBX',
                            url: await DownloadHelper.uploadUrlToFileStorage(url, filename)
                        };
                    }
                )
            );
            itemCount++;
        }

        if (killProcess != null && killProcess()) {
            return null;
        }

        if (exportSettings.indexOf('OBK') !== -1) {
            sessionExports.push(
                await DownloadHelper.exportObk().then(
                    async (url: string) => {
                        return {
                            format: 'OBK',
                            url: await DownloadHelper.uploadUrlToFileStorage(url, filename)
                        };
                    }
                )
            );
            itemCount++;
        }
        shareContent.session = sessionExports;
        if (itemCount === 0) {
            return null;
        }
        return shareContent;
    }

    public static async getMailOptions(exportSettings: ToggleStoreState, killProcess: () => boolean): Promise<MailOptions | null> {
        const shareContent: MailShareContent | null = await this.generateMailShareContent(exportSettings, killProcess);

        if (shareContent == null) {
            return null;
        }
        let shareLinks: Array<ExportMapping> = [];
        if (shareContent.cad != null) {
            shareLinks = shareLinks.concat(shareContent.cad);
        }

        if (shareContent.session != null) {
            shareLinks = shareLinks.concat(shareContent.session);
        }

        shareLinks.map((value: ExportMapping): void => {
            Stats.sendEvent({
                category: 'Send',
                action: 'e-mail',
                label: value.format
            });
        });

        if (shareContent.articleList != null) {
            Stats.sendEvent({
                category: 'Send',
                action: 'e-mail',
                label: 'PDF'
            });
        }

        if (shareContent.image != null) {
            Stats.sendEvent({
                category: 'Send',
                action: 'e-mail',
                label: PersistentStore.getState().imageFormat
            });
        }

        return getMailOptions(shareContent, shareLinks, exportSettings);
    }
}

export function linkInfo(): string {
    const currentDate: Date = new Date();

    currentDate.setMonth(currentDate.getMonth() + 1);

    const date: string = currentDate.toLocaleDateString(PersistentStore.getState().language);

    return [
        t('Hello,') + '\r\n',
        '\r\n',
        t('using the B.PRO 3D configurator, I have created following solution(s) for you - see download links below.') + ' ',
        t('These links are valid until {{- date}}.', { date }) + ' ',
        t('I am looking forward to your feedback.') + '\r\n',
    ].join('');
}

async function getTextEmailBody(shareContent: MailShareContent, shareLinks: Array<ExportMapping>): Promise<string> {
    let body: string = '';
    let linkInfoNeeded: boolean = false;

    if (shareContent.planning != null) {
        linkInfoNeeded = true;
        body += '\r\n' + t('3D configuration') + ': ' + shareContent.planning + '\r\n';
    }

    const infoLinks: Array<ExportMapping> = shareLinks.filter((shareLink: ExportMapping) => {
        return (
            shareLink.format === 'OBK' ||
            shareLink.format === 'OBX'
        );
    });

    const cadLinks: Array<ExportMapping> = shareLinks.filter((shareLink: ExportMapping) => {
        return !(
            shareLink.format === 'OBK' ||
            shareLink.format === 'OBX'
        );
    });

    if (infoLinks.length) {
        linkInfoNeeded = true;
        body += '\r\n' + t('Commercial Information') + ':';
        body += '\r\n' + infoLinks.map((value: ExportMapping): string => {
            return value.format + ': ' + value.url;
        }).join('\r\n') + '\r\n';
    }

    if (cadLinks.length) {
        linkInfoNeeded = true;
        body += '\r\n' + t('CAD Symbols') + ':';
        body += '\r\n' + cadLinks.map((value: ExportMapping): string => {
            return value.format + ': ' + value.url;
        }).join('\r\n');
    }

    body += '\r\n\r\n';
    body += t('You can find the configurator here: {{- url}}', {url: 'https://configurator.bpro-solutions.com'});
    body += '\r\n';
    body += t('For more information about B.PRO go to: {{- url}}', {url: 'https://www.bpro-solutions.com'});
    body += '\r\n\r\n\r\n';
    body += t('With best regards,');
    body += '\r\n';

    if (linkInfoNeeded) {
        body = linkInfo() + body;
    }

    return body;
}

export async function getMailOptions(shareContent: MailShareContent, shareLinks: Array<ExportMapping>, exportSettings: ToggleStoreState): Promise<MailOptions> {
    const attachments: MailOptions['attachments'] = [];

    const filename: string = ProjectManagerStore.getProjectName(true);

    if (exportSettings.indexOf('IMAGE') !== -1 && shareContent.image != null) {
        const imageFilename: string = filename + '.png';
        await writeFile('/tmp/image.png', shareContent.image);
        attachments.push({
            name: imageFilename,
            path: '/tmp/image.png',
            mimeType: 'image/png'
        });
    }

    if (shareContent.articleList != null) {
        const articleListFilename: string = filename + '.pdf';
        await writeFile('/tmp/articlelist.pdf', shareContent.articleList);
        attachments.push({
            name: articleListFilename,
            path: '/tmp/articlelist.pdf',
            mimeType: 'application/pdf'
        });
    }
    if (shareContent.excel != null) {
        const excelFileName: string = filename + '.xlsx';
        await writeFile('/tmp/excel.xlsx', shareContent.excel);
        attachments.push({
            name: excelFileName,
            path: '/tmp/excel.xlsx',
            mimeType: XlsxPopulate.MIME_TYPE
        });
    }

    if (AppConfigurationStore.getState().os === 'AndroidOS') {
        return {
            body: await getTextEmailBody(shareContent, shareLinks),
            subject: t('3D configuration with products from B.PRO'),
            attachments,
            html: false,
        };
    }

    // tslint:disable-next-line:max-line-length
    const textTemplate: string = '<div style="cursor:auto;color:#000000;font-size:14px;font-weight:400;line-height:22px;text-align:left;">#TEXT#</div>';
    // tslint:disable-next-line:max-line-length
    const linkTemplate: string = '<div style="cursor:auto;color:#000000;font-size:14px;font-weight:400;line-height:22px;text-align:left;"><a style="color:#7da50b;text-decoration: none;" href="#LINKURL#">#LINKNAME#&nbsp;&raquo;</a></div>';
    let linkInfoNeeded: boolean = false;

    let mailContent: string = '';
    if (shareContent.planning != null) {
        linkInfoNeeded = true;
        mailContent += '\r\n<br>' + textTemplate.replace('#TEXT#', t('3D configuration') + ':') + '\r\n';
        mailContent += '\r\n' + linkTemplate.replace('#LINKURL#', shareContent.planning).replace('#LINKNAME#', 'PBOX') + '\r\n';
    }
    if (shareLinks.length) {
        linkInfoNeeded = true;
        const infoLinks: Array<ExportMapping> = shareLinks.filter((shareLink: ExportMapping) => {
            return (
                shareLink.format === 'OBK' ||
                shareLink.format === 'OBX'
            );
        });
        const cadLinks: Array<ExportMapping> = shareLinks.filter((shareLink: ExportMapping) => {
            return !(
                shareLink.format === 'OBK' ||
                shareLink.format === 'OBX'
            );
        });
        if (infoLinks.length) {
            mailContent += '\r\n<br>' + textTemplate.replace('#TEXT#', t('Commercial Information') + ':') + '\r\n';
            mailContent += '\r\n' + infoLinks.map((value: ExportMapping): string => {
                return linkTemplate.replace('#LINKURL#', value.url).replace('#LINKNAME#', value.format);
            }).join('\r\n');
        }
        if (cadLinks.length) {
            mailContent += '\r\n<br>' + textTemplate.replace('#TEXT#', t('CAD Symbols') + ':') + '\r\n';
            mailContent += '\r\n' + cadLinks.map((value: ExportMapping): string => {
                return linkTemplate.replace('#LINKURL#', value.url).replace('#LINKNAME#', value.format);
            }).join('\r\n');
        }
        mailContent += '\r\n<br>';
    }

    if (
        (exportSettings.indexOf('IMAGE') !== -1 && shareContent.image != null) ||
        (shareContent.articleList != null)
    ) {
        mailContent += textTemplate.replace('#TEXT#', t('Attachment') + ':') + '\r\n';
        if (exportSettings.indexOf('IMAGE') !== -1 && shareContent.image != null) {
            mailContent += '\r\n' + textTemplate.replace('#TEXT#', t('Image')) + '\r\n';
        }
        if (shareContent.articleList != null) {
            mailContent += '\r\n' + textTemplate.replace('#TEXT#', t('Article List')) + '\r\n';
        }
    }

    if (linkInfoNeeded) {
        mailContent = textTemplate.replace('#TEXT#', linkInfo()) + mailContent;
    }

    const html: string = await utils.async.ajax<string>('GET', 'res/mail/mail-template.html', null, { dataType: 'text' });

    return {
        body: html.replace('##CONTENT##', mailContent),
        subject: t('3D configuration with products from B.PRO'),
        attachments,
        html: true,
    };
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/utils/MailHelper.ts